import { ReactNode, useRef, useState, useEffect } from 'react';
import { Box, Flex, IconButton, Icon, Text, Spinner } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import FilterChips, { FilterOption } from './filter-chips';

type Props = {
  title: string;
  filters?: FilterOption[];
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  primaryFilters?: FilterOption[];
  primaryFilterValue?: string;
  onPrimaryFilterChange?: (value: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyState?: ReactNode;
  children: ReactNode;
};

const CARD_WIDTH = 280;

export default function ContentCarousel({
  title,
  filters,
  filterValue,
  onFilterChange,
  primaryFilters,
  primaryFilterValue,
  onPrimaryFilterChange,
  isLoading,
  emptyMessage = 'Nothing to show yet.',
  emptyState,
  children,
}: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [children]);

  const scrollBy = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = direction * (CARD_WIDTH * 2);
    el.scrollBy({ left: distance, behavior: 'smooth' });
  };

  const childArray = Array.isArray(children) ? children : [children];
  const isEmpty = !isLoading && childArray.flat().filter(Boolean).length === 0;
  const showFilters =
    filters && filters.length > 0 && filterValue !== undefined && onFilterChange;
  const showPrimaryFilters =
    primaryFilters &&
    primaryFilters.length > 0 &&
    primaryFilterValue !== undefined &&
    onPrimaryFilterChange;

  const itemCount = childArray.flat().filter(Boolean).length;

  return (
    <Box mb={8}>
      <Flex justify='space-between' align='baseline' mb={3} gap={4}>
        <Flex align='baseline' gap={2}>
          <Text
            fontSize={{ base: 'lg', md: 'xl' }}
            fontWeight='bold'
            color='gray.900'
            letterSpacing='-0.01em'
          >
            {title}
          </Text>
          {itemCount > 0 && (
            <Text fontSize='sm' fontWeight='medium' color='gray.400'>
              {itemCount}
            </Text>
          )}
        </Flex>
        <Flex gap={1.5}>
          <IconButton
            aria-label='Scroll left'
            size='sm'
            variant='outline'
            bg='white'
            borderColor='gray.200'
            borderRadius='full'
            shadow='0 1px 2px rgba(15,23,42,0.04)'
            disabled={!canScrollLeft}
            onClick={() => scrollBy(-1)}
            _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
          >
            <Icon as={ChevronLeftIcon} w={4} h={4} />
          </IconButton>
          <IconButton
            aria-label='Scroll right'
            size='sm'
            variant='outline'
            bg='white'
            borderColor='gray.200'
            borderRadius='full'
            shadow='0 1px 2px rgba(15,23,42,0.04)'
            disabled={!canScrollRight}
            onClick={() => scrollBy(1)}
            _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
          >
            <Icon as={ChevronRightIcon} w={4} h={4} />
          </IconButton>
        </Flex>
      </Flex>
      {showPrimaryFilters && (
        <Box mb={2}>
          <FilterChips
            options={primaryFilters!}
            value={primaryFilterValue!}
            onChange={onPrimaryFilterChange!}
          />
        </Box>
      )}
      {showFilters && (
        <Box mb={3}>
          <FilterChips options={filters!} value={filterValue!} onChange={onFilterChange!} />
        </Box>
      )}
      {isLoading && (
        <Flex align='center' justify='center' py={6}>
          <Spinner size='sm' color='gray.400' />
        </Flex>
      )}
      {isEmpty && !isLoading && (
        <>
          {emptyState ?? (
            <Box py={8} textAlign='center'>
              <Text fontSize='sm' color='gray.500'>
                {emptyMessage}
              </Text>
            </Box>
          )}
        </>
      )}
      {!isLoading && !isEmpty && (
        <Flex
          ref={scrollRef}
          gap={4}
          overflowX='auto'
          alignItems='stretch'
          scrollSnapType='x mandatory'
          pb={2}
          css={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {childArray.flat().map((child, idx) =>
            child ? (
              <Box
                key={idx}
                flex='0 0 auto'
                width={`${CARD_WIDTH}px`}
                scrollSnapAlign='start'
                display='flex'
              >
                {child}
              </Box>
            ) : null,
          )}
        </Flex>
      )}
    </Box>
  );
}
